/**
 * Sensitive Fields Registry
 * 
 * Centralized configuration for field sensitivity levels.
 * Used by SensitiveField component to determine default masking behavior.
 * 
 * COMPLIANCE NOTE: This file serves as auditable documentation of
 * which fields contain sensitive data and why they're protected.
 */

export type SensitivityLevel = 'public' | 'internal' | 'sensitive' | 'phi';

export interface FieldSensitivityConfig {
  level: SensitivityLevel;
  reason: string;
  maskPattern?: string; // Optional custom mask pattern
}

/**
 * Field Sensitivity Registry
 * 
 * Maps field names to their sensitivity configuration.
 * Field names should match the property names used in the data models.
 */
export const FIELD_SENSITIVITY_REGISTRY: Record<string, FieldSensitivityConfig> = {
  // ─────────────────────────────────────────────────────────────
  // PHI Fields (Protected Health Information)
  // These fields may reveal health conditions or medical history
  // ─────────────────────────────────────────────────────────────
  
  'experience_name': {
    level: 'phi',
    reason: 'Exploration names may reveal health conditions (e.g., "Diabetes Management Program"). Protected under HIPAA.',
  },
  
  'exploration_name': {
    level: 'phi',
    reason: 'Alias for experience_name. May reveal health conditions.',
  },

  // ─────────────────────────────────────────────────────────────
  // Sensitive Fields
  // May contain PHI in free-text form, requires careful handling
  // ─────────────────────────────────────────────────────────────
  
  'transaction_notes': {
    level: 'phi',
    reason: 'Free-text field that may contain PHI or PII entered by support staff. Treated as PHI for HIPAA compliance.',
  },

  'internal_notes': {
    level: 'phi',
    reason: 'Free-text internal notes field that may contain PHI or PII entered by admin staff. Treated as PHI for HIPAA compliance.',
  },
  
  'notes': {
    level: 'sensitive',
    reason: 'Generic notes field that may contain sensitive information.',
  },
  
  'context_notes': {
    level: 'sensitive',
    reason: 'Context notes may contain PHI or operational details.',
  },

  // ─────────────────────────────────────────────────────────────
  // Internal Fields
  // Not PHI but should be treated carefully, limited visibility
  // ─────────────────────────────────────────────────────────────
  
  'ref_experience_id': {
    level: 'internal',
    reason: 'Internal reference ID. Not PHI but reveals system structure.',
  },
  
  'exploration_signup_id': {
    level: 'internal',
    reason: 'Internal signup tracking ID.',
  },
  
  'original_transaction_id': {
    level: 'internal',
    reason: 'Internal transaction reference for adjustments.',
  },
  
  'assigned_by': {
    level: 'internal',
    reason: 'Staff email who made manual assignment.',
  },
  
  'ticket_id': {
    level: 'internal',
    reason: 'Support ticket reference.',
  },

  // ─────────────────────────────────────────────────────────────
  // Public Fields (explicitly safe)
  // These are safe to display without masking
  // ─────────────────────────────────────────────────────────────
  
  'reward_type': {
    level: 'public',
    reason: 'Generic category, does not reveal health information.',
  },
  
  'points': {
    level: 'public',
    reason: 'Point values are not sensitive.',
  },
  
  'migration_source': {
    level: 'public',
    reason: 'System migration metadata.',
  },
  
  'migrated_at': {
    level: 'public',
    reason: 'Migration timestamp.',
  },
};

/**
 * Get sensitivity configuration for a field
 * 
 * @param fieldName - The field name to look up
 * @returns FieldSensitivityConfig or undefined if not in registry
 */
export function getFieldSensitivity(fieldName: string): FieldSensitivityConfig | undefined {
  return FIELD_SENSITIVITY_REGISTRY[fieldName];
}

/**
 * Get sensitivity level for a field, with default fallback
 * 
 * @param fieldName - The field name to look up
 * @param defaultLevel - Default level if not in registry (default: 'public')
 * @returns SensitivityLevel
 */
export function getFieldSensitivityLevel(
  fieldName: string,
  defaultLevel: SensitivityLevel = 'public'
): SensitivityLevel {
  const config = FIELD_SENSITIVITY_REGISTRY[fieldName];
  return config?.level ?? defaultLevel;
}

/**
 * Check if a field requires masking (sensitive or phi)
 * 
 * @param fieldName - The field name to check
 * @returns boolean - true if field should be masked by default
 */
export function requiresMasking(fieldName: string): boolean {
  const level = getFieldSensitivityLevel(fieldName);
  return level === 'phi' || level === 'sensitive';
}

/**
 * Sensitivity level display configuration
 */
export const SENSITIVITY_DISPLAY: Record<SensitivityLevel, {
  label: string;
  color: string;
  icon: string;
  description: string;
}> = {
  public: {
    label: 'Public',
    color: 'success',
    icon: '🌐',
    description: 'Safe to display',
  },
  internal: {
    label: 'Internal',
    color: 'info',
    icon: '🔒',
    description: 'Internal use only',
  },
  sensitive: {
    label: 'Sensitive',
    color: 'warning',
    icon: '⚠️',
    description: 'May contain PHI - handle with care',
  },
  phi: {
    label: 'PHI',
    color: 'danger',
    icon: '🏥',
    description: 'Protected Health Information - HIPAA protected',
  },
};

